import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Logger } from "../../infra/logger/logger";
import salaryRepository from "../../infra/repositories/salary-repository";
import { SalaryInputFilter } from "domain/models/salary-input-filter";
import { Salary } from "domain/entities/salary";

export const useSalariesByFilter = (filter: SalaryInputFilter) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["Salaries", filter],
    queryFn: async () => {
      try {
        const result = await salaryRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata,
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
    enabled: !filter.employeeId || filter.employeeId == "" ? false : true,
  });
};

export const useSalariesById = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["salary", id],
    queryFn: async () => {
      try {
        if (!id) throw new Error("Id field invalid");
        const result = await salaryRepository.getById(id);

        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata,
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useCreatesalaryMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Salary) => {
      const result = await salaryRepository.create(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["Salaries", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
        } else {
          logger.error("Erro desconhecido da API", error.response?.data);
        }
      } else {
        logger.error("Erro desconhecido", error);
      }
    },
  });
};

export const useUpdatesalaryMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Salary) => {
      const result = await salaryRepository.update(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["Salaries", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
        } else {
          logger.error("Erro desconhecido da API", error.response?.data);
        }
      } else {
        logger.error("Erro desconhecido", error);
      }
    },
  });
};

export const useDeletesalaryMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Salary) => {
      const result = await salaryRepository.update(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["Salaries", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata,
        );
        throw new Error(error.response?.data.metadata.errors);
      }
      throw error;
    },
  });
};
